import Store from 'store/store';
import { getData } from 'utils/dynamicData';
import * as categories from 'utils/gdpr/categories';
import { waitForOneTrust } from 'utils/gdpr/oneTrust';

const GA_IDS = [
	'G-W6FD9FK0F1',
	'G-V2SVFSPN4W',
	'G-VMK0X6QJJD',
];
export const gaScript = typeof window !== 'undefined' && isOneTrustAccepted() ? `https://www.googletagmanager.com/gtag/js?id=${GA_IDS[0]}` : null;
export const gaInit = `
window.dataLayer = window.dataLayer || [];
window.gtag = function gtag() { dataLayer.push(arguments); }`;

const getPagePath = page => page.id === 'movie' ? `/films/${page.props?.slug}/` : page.path;

let currentMovieSlug = typeof window !== 'undefined' ? window.__MOVIE_DATA__?.slug : undefined;

if (typeof window !== 'undefined') {
	// Initialize tracking after OneTrust is ready
	//set gtag consent
	let currentConsent = { ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied', analytics_storage: 'denied' };
	gtag('consent', 'default', currentConsent);
	waitForOneTrust().then(() => {
		if (isOneTrustAccepted()) {
			const gtagGrantAllConsent = () => {
				const adStorage = categories.has('TARGETING') ? 'granted' : 'denied';
				const analyticsStorage = categories.has('PERFORMANCE') ? 'granted' : 'denied';
				let newConsent = { ad_storage: adStorage, ad_user_data: adStorage, ad_personalization: adStorage, analytics_storage: analyticsStorage };
				if (JSON.stringify(newConsent) !== JSON.stringify(currentConsent)) {
					currentConsent = newConsent;
					gtag('consent', 'update', currentConsent);
					dataLayer.push({ event: 'gtm.update_consent' });
				}
			};
			gtagGrantAllConsent();
			categories.addChangeListener(gtagGrantAllConsent);

			gtag('js', new Date());
		}
	});
}

const pageTypes = {};

// NOTE: keep simple (no call to other functions) as it is injected in the HTML
export function getDefaultDataLayerVariables() {
	return {
		url: location.href,
		url_cleaned: location.origin + location.pathname.replace(/\/$/, ''),
		domain: location.hostname,
		market: __REGION__,
		referrer: location.href,
		params: location.search,
		template: 'powsterfocus',
	};
}

// NOTE: keep simple (no call to other functions) as it is injected in the HTML
export function getMovieVariablesFromMovieObject(m, s) {
	return {
		movie: m?.slug || s || null,
		movie_name: m?.title || null,
		imdb_id: m?.dataLayer?.imdbId || m?.imdb || null,
		initial_release_date: m?.dates?.release || null,
		genre_1: m?.dataLayer?.magicGenre || m?.genres?.magic || null,
		essence_genre: m?.dataLayer?.essenceGenre || m?.genres?.essence || null,
	};
}

async function getMovieVariables(slug) {
	currentMovieSlug = slug;
	const movieData = slug && await getData(slug, 'movie');
	return getMovieVariablesFromMovieObject(movieData, slug);
}

export async function updateDataLayer(variables) {
	const movie = variables?.movie || Store.get().activePageProps?.slug;
	dataLayer.push({
		...getDefaultDataLayerVariables(),
		...(movie !== currentMovieSlug ? await getMovieVariables(movie) : {}),
		...variables,
	});
}

function isOneTrustAccepted() {
	// Check if OneTrust is loaded first
	if (!window.OptanonActiveGroups) {
		return false;
	}

	return window.OptanonActiveGroups && window.OptanonActiveGroups.indexOf('2') > -1;
}

let didConfig = false;
let dwellTimer;
let pendingPageViews = [];
let isProcessingPending = false;

// Process all pending page views when OneTrust becomes available
async function processPendingPageViews() {
	if (isProcessingPending || pendingPageViews.length === 0) return;
	isProcessingPending = true;

	try {
		await waitForOneTrust();

		if (isOneTrustAccepted()) {
			const viewsToProcess = [...pendingPageViews];
			pendingPageViews = []; // Clear the queue

			for (const page of viewsToProcess) {
				await trackPageViewInternal(page); // Use internal function to avoid recursion
			}
		}
	} catch (error) {
		console.error('Error processing pending page views:', error);
	} finally {
		isProcessingPending = false;
	}
}

// Listen for OneTrust events
if (typeof window !== 'undefined') {
	window.addEventListener('consent.onetrust', processPendingPageViews);
	window.addEventListener('OneTrustGroupsUpdated', processPendingPageViews);
}

export async function trackPageView(page) {
	// Wait for OneTrust to be loaded before checking consent
	await waitForOneTrust();

	//check if consent given for analytics
	if (!isOneTrustAccepted()) {
		// Add to pending queue
		pendingPageViews.push(page);
		return;
	}

	// Process this page view immediately
	await trackPageViewInternal(page);
}

async function trackPageViewInternal(page) {
	const pagePath = getPagePath(page);
	if (!didConfig) {
		didConfig = true;
		GA_IDS.forEach(id => {
			// page_path: '{{page}}',
			// custom_map: { 'dimension1': 'region' },
			// event_stream: '{{app}}',
			// event_region: '{{region}}',
			// event_studio: '{{studioName}}',
			// region: '{{region}}',
			gtag('config', id, {
				anonymize_ip: true,
				allow_google_signals: false,
				send_page_view: false,
				page_path: pagePath,
				debug_mode: false,
				cookie_flags: window !== window.top ? 'SameSite=None; Secure' : '',
			});
		});
	}
	clearTimeout(dwellTimer);
	updateDataLayer({
		event: 'pageview',
		page_type: pageTypes[page.id] || page.name || page.id,
		page_name: document.title,
	});
	gtag('event', 'page_view', { send_to: GA_IDS, allow_google_signals: false, page_path: pagePath });
	track('pageload', 'timing_complete', '', { name: 'load', value: Math.round(window.startLoadTime) });
	const pixelRatio = window.devicePixelRatio || 1;
	track('tech-details', 'screen-pixel-dimensions', `${window.innerWidth * pixelRatio}x${window.innerHeight * pixelRatio}`);
	dwellTimer = setTimeout(() => dataLayer.push({ event: 'threesecdwell' }), 3000);
}

export function track(category, action, label, customData) {
	if (!isOneTrustAccepted()) return; // Do not track if consent not given

	updateDataLayer({
		event_action: action,
		movie: customData?.movie
	});

	gtag('event', action, {
		send_to: GA_IDS,
		event_category: category,
		event_label: label,
		allow_google_signals: false,
		...customData,
	});
}
